/**
 * Sprint 34 — Platform Operator impersonation (salt-okunur müşteri görüntüleme).
 *
 * Platform staff `CustomerDetailPage`'de "Müşteri olarak görüntüle" der → backend
 * ephemeral JWT (`{cmp, role:OWNER, pst:true, imp:true, exp:30dk}`) döner ve yeni
 * sekme `/?imp=<token>` ile açılır.
 *
 * Token NORMAL access cookie yerine `sessionStorage`'da tutulur:
 *   - tab-specific (sekme kapanınca silinir),
 *   - kalıcı login cookie'sini ezmez,
 *   - api client'ı `Authorization: Bearer` header'ı ile gönderir (JwtStrategy
 *     hem cookie hem bearer kabul ediyor).
 */

const IMP_TOKEN_KEY = 'custel_imp_token';

interface ImpersonationClaims {
  exp?: number; // saniye (epoch)
  cmp?: string; // companyId
  imp?: boolean;
  role?: string;
}

/** JWT payload segmentini decode et (imza DOĞRULAMAZ — sadece claim okuma). */
function decodeJwtPayload(token: string): ImpersonationClaims | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as ImpersonationClaims;
  } catch {
    return null;
  }
}

/**
 * Uygulama mount' undan ÖNCE (main.tsx) çağrılır.
 * `?imp=<token>` URL parametresini yakalar, geçerli impersonation token'ı ise
 * sessionStorage'a alır ve parametreyi URL'den temizler (token adres çubuğunda
 * görünmesin / yer imine kaydedilmesin).
 */
export function captureImpersonationToken(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('imp');
  if (!token) return;

  const claims = decodeJwtPayload(token);
  const isValid = claims?.imp === true && !!claims.cmp && (claims.exp ?? 0) * 1000 > Date.now();

  if (isValid) {
    try {
      window.sessionStorage.setItem(IMP_TOKEN_KEY, token);
    } catch {
      /* private mode — yoksay */
    }
  }

  // Token'ı kullanılır kullanılmaz URL'den temizle.
  params.delete('imp');
  const newSearch = params.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
  window.history.replaceState(null, '', newUrl);
}

/** Aktif impersonation token'ı (yoksa null). Süresi dolmuşsa temizler. */
export function getImpersonationToken(): string | null {
  if (typeof window === 'undefined') return null;
  let token: string | null = null;
  try {
    token = window.sessionStorage.getItem(IMP_TOKEN_KEY);
  } catch {
    return null;
  }
  if (!token) return null;

  const claims = decodeJwtPayload(token);
  if (!claims || (claims.exp ?? 0) * 1000 <= Date.now()) {
    clearImpersonation();
    return null;
  }
  return token;
}

export function isImpersonating(): boolean {
  return getImpersonationToken() !== null;
}

/** Token exp claim'i (ms cinsinden epoch) — geri sayım için. */
export function getImpersonationExpiresAt(): number | null {
  const token = getImpersonationToken();
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  return claims?.exp ? claims.exp * 1000 : null;
}

export function clearImpersonation(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(IMP_TOKEN_KEY);
  } catch {
    /* yoksay */
  }
}

/** İmpersonation'dan çık → token temizle, platform konsoluna dön. */
export function exitImpersonation(): void {
  clearImpersonation();
  if (typeof window !== 'undefined') {
    window.location.href = '/platform';
  }
}
